import { css } from 'astroturf';
import Prism from 'prismjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const CONTENT_HEIGHT = 620;
const PULL_MAX = 70;
const PULL_TRIGGER = 46;

const styles = css`
  .demo {
    background: linear-gradient(135deg, rgba(34, 95, 197, 0.16), rgba(31, 160, 122, 0.16));
    border-radius: 16px;
    box-shadow: rgba(10, 37, 99, 0.2) 0.3em 0.3em 2em inset;
    padding: 1.5rem;
    margin: 1.75rem 0;

    @media (max-width: 600px) {
      padding: 1rem;
    }
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: 0.9rem;
    margin-bottom: 1rem;
    align-items: flex-end;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .field label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #355;
  }

  .inlineField {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .inlineField label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #355;
  }

  .topBar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.9rem;
  }

  .field select,
  .inlineField select {
    appearance: auto;
    background: rgba(255, 255, 255, 0.7);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    border-radius: 8px;
    padding: 0.4rem 0.7rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #16335c;
    cursor: pointer;
  }

  .field select:hover,
  .inlineField select:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  .checkboxField {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #234;
  }

  .checkboxField input {
    accent-color: #0f63d8;
    width: 16px;
    height: 16px;
  }

  .smallButton {
    background: rgba(255, 255, 255, 0.7);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    color: #16335c;
    border-radius: 8px;
    padding: 0.45rem 0.8rem;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s;
  }

  .smallButton:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.95);
  }

  .smallButton:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .segmentedToggle {
    display: flex;
    flex-grow: 1;
    flex-wrap: wrap;
    border-radius: 8px;
    overflow: hidden;
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    background: rgba(255, 255, 255, 0.6);
  }

  .segmentedToggleBtn {
    padding: 0.45rem 0.9rem;
    border: none;
    background: transparent;
    cursor: pointer;
    flex: 1 1 33%;
    font-size: 0.75rem;
    font-weight: 700;
    color: rgba(22, 51, 92, 0.5);
    white-space: nowrap;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .segmentedToggleBtn + .segmentedToggleBtn {
    border-left: 1.5px solid rgba(14, 19, 35, 0.18);
  }

  .segmentedToggleBtnActiveBad {
    background: #b91c3c;
    color: #fff;
  }

  .segmentedToggleBtnActiveGood {
    background: #05a179;
    color: #fff;
  }

  /* ---- basic scroll container demo ---- */

  .basicBox {
    background: rgb(30, 30, 63);
    color: #fff;
    border-radius: 10px;
    padding: 0.9rem 1rem;
    font-size: 0.9rem;
    line-height: 1.55;
    box-shadow: 0 4px 18px rgba(18, 44, 89, 0.12);
  }

  .basicBox p {
    margin: 0 0 0.85rem;
  }

  .basicBox p:last-child {
    margin-bottom: 0;
  }

  .badgeGroup {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .containerBadge {
    align-self: flex-start;
    border-radius: 999px;
    padding: 0.35rem 0.8rem;
    font-size: 0.75rem;
    line-height: 1;
    font-weight: 700;
    color: #fff;
  }

  .containerBadgeYes {
    background: #0f63d8;
  }

  .containerBadgeNo {
    background: rgba(22, 51, 92, 0.45);
  }

  .scrolledBadge {
    border-radius: 999px;
    padding: 0.35rem 0.8rem;
    font-size: 0.75rem;
    line-height: 1;
    font-weight: 700;
    color: #fff;
    background: #05a179;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .scrolledBadgeVisible {
    opacity: 1;
  }

  /* ---- iframe preview shell (shared by propagation + shell demos) ---- */

  .previewShell {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(220px, 1fr);
    gap: 1rem;
    align-items: start;
  }

  @media (max-width: 640px) {
    .previewShell {
      grid-template-columns: 1fr;
    }
  }

  .previewFrame {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    background: #eef5ff;
    box-shadow: 0 8px 24px rgba(6, 17, 43, 0.18);
    border: 1px solid rgba(14, 19, 35, 0.14);
  }

  .previewFrame iframe {
    width: 100%;
    height: 280px;
    border: 0;
    display: block;
    background: #fff;
  }

  .readout {
    background: #121a32;
    color: #d7f0ff;
    border-radius: 10px;
    padding: 0.9rem 1rem;
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .readoutRow {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.25rem 0;
  }

  .readoutRow:last-child {
    border-bottom: 0;
  }

  .readoutLabel {
    color: #8fb4e0;
  }

  .readoutValueGood {
    color: #6be8b8;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }

  .readoutValueBad {
    color: #ff9db0;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }

  .readoutValueNeutral {
    color: #d7f0ff;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }

  .readoutSelect {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    color: #f2f8ff;
    font-family: inherit;
    font-size: 0.75rem;
    padding: 0.15rem 0.4rem;
    cursor: pointer;
  }

  .readoutSelect option {
    color: #0f2a44;
    background: #fff;
  }

  .readoutCheckbox {
    accent-color: #6be8b8;
    width: 15px;
    height: 15px;
    cursor: pointer;
  }

  .readoutCheckboxLabel {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #d7f0ff;
    cursor: pointer;
  }

  .readoutStack {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .propagationTable {
    width: 100%;
    border-collapse: collapse;
  }

  .propagationTable th,
  .propagationTable td {
    padding: 0.5rem !important;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .propagationTable th:first-child,
  .propagationTable td:first-child {
    padding-left: 0 !important;
  }

  .propagationTable th:last-child,
  .propagationTable td:last-child {
    padding-right: 0;
    padding-bottom: 0.5rem;
    text-align: right;
  }

  .propagationTable tr:last-child td {
    border-bottom: 0;
    padding-bottom: 0.5rem;
  }

  .propagationTable th {
    color: #8fb4e0;
    font-weight: 400;
  }

  .readout .propagationTable thead {
    background: transparent;
    border-radius: 0;
  }

  .propagationTable td:first-child {
    color: #8fb4e0;
    white-space: nowrap;
  }

  .codeBlockWrap {
    margin-top: 0.9rem;

    & :global(.gatsby-highlight) {
      margin: 0;
      border-radius: 10px;
      overflow: auto;
      background: #171734;
    }

    & :global(pre[class*='language-']) {
      margin: 0;
      padding: 1rem 1.2rem;
      font-size: 0.8rem;
      line-height: 1.5;
      white-space: pre;
    }
  }

  /* ---- side-by-side scrollY comparison ---- */

  .sideBySideGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  @media (max-width: 640px) {
    .sideBySideGrid {
      grid-template-columns: 1fr;
    }
  }

  .sideBySidePane {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .sideBySideLabel {
    font-size: 0.875rem;
    font-weight: 700;
    color: #234;
  }

  .scrollYBox {
    background: #121a32;
    border-radius: 10px;
    padding: 0.7rem 1rem;
    text-align: center;
  }

  .scrollYValue {
    font-size: 1.5rem;
    font-weight: 800;
  }

  .scrollYValueGood {
    color: #6be8b8;
  }

  .scrollYValueBad {
    color: #ff9db0;
  }

  .scrollYCaption {
    font-size: 0.75rem;
    color: #8fb4e0;
    margin-top: 0.2rem;
  }

  /* ---- dialog / modal lock demo ---- */

  .dialogControlsRow {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.7rem;
    margin-bottom: 1rem;
  }

  /* ---- reload button (mirrors the css random demo's randomize button) ---- */

  .reloadButton {
    background: rgba(255, 255, 255, 0.6);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    color: #16335c;
    border-radius: 8px;
    padding: 0.4rem 0.7rem;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: background 0.15s;

    &:active svg {
      scale: 0.75;
      transform: rotate(-10deg);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.85);
    }
  }

  @keyframes reloadSpin {
    from {
      rotate: 0deg;
    }
    to {
      rotate: 360deg;
    }
  }

  .reloadIcon {
    will-change: rotate, scale, transform;
    transition:
      scale 0.15s ease-in-out,
      transform 0.15s ease-in-out;
    animation: reloadSpin 0.5s ease-out both;
  }

  /* ---- phone frame (mobile demo) ---- */

  .phoneCol {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  .phoneViewport {
    position: relative;
    width: 100%;
    max-width: 260px;
    aspect-ratio: 9 / 19;
    margin: 0 auto;
  }

  .phoneFrame {
    position: absolute;
    inset: 0;
    border-radius: 24px;
    overflow: hidden;
    border: 8px solid #0e1323;
    background: #eef5ff;
    box-shadow: 0 14px 36px rgba(6, 17, 43, 0.22);
    touch-action: none;
    user-select: none;
  }

  .phoneScreen {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, #f8fbff, #e9f2ff 45%, #f7fff7);
  }

  .phoneStatusBar {
    position: relative;
    z-index: 6;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0e1323;
    cursor: pointer;
  }

  .phoneNotch {
    width: 34%;
    max-width: 96px;
    height: 18px;
    border-radius: 999px;
    background: #090d17;
  }

  .phoneAddressBar {
    position: relative;
    z-index: 5;
    height: 34px;
    flex-shrink: 0;
    background: rgba(14, 18, 30, 0.94);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    display: flex;
    align-items: center;
    transition: margin-top 0.25s ease;
  }

  .phoneAddressBarBody {
    margin: 0 10px;
    flex: 1;
    height: 18px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .phoneContentOuter {
    position: relative;
    flex: 1;
    overflow: hidden;
  }

  .phoneContentNestedBox {
    position: absolute;
    inset: 10px 8px 44px;
    border-radius: 12px;
    border: 2px dashed #b91c3c;
    overflow: hidden;
    background: #fff;
  }

  .phoneContentInner {
    position: relative;
    padding: 10px;
  }

  .phonePullIndicator {
    position: absolute;
    top: -40px;
    left: 50%;
    transform: translateX(-50%);
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background: #0e1323;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    opacity: 0;
  }

  .phonePullIndicatorVisible {
    opacity: 1;
  }

  @keyframes pullRefreshSpin {
    from {
      rotate: 0deg;
    }
    to {
      rotate: 360deg;
    }
  }

  .phonePullIndicatorSpinning svg {
    animation: pullRefreshSpin 0.6s linear infinite;
  }

  .phoneCard {
    background: #fff;
    border: 1px solid #dce8ff;
    border-radius: 10px;
    padding: 0.5rem 0.6rem;
    margin-bottom: 0.5rem;
    font-size: 0.7rem;
    color: #234;
    box-shadow: 0 3px 10px rgba(18, 44, 89, 0.08);
  }

  .phoneHomeIndicator {
    position: absolute;
    left: 50%;
    bottom: 8px;
    transform: translateX(-50%);
    width: 34%;
    max-width: 90px;
    height: 4px;
    border-radius: 999px;
    background: rgba(15, 30, 55, 0.35);
    z-index: 6;
  }

  .phoneBadge {
    border-radius: 999px;
    padding: 0.35rem 0.8rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #fff;
    text-align: center;
  }

  .phoneBadgeGood {
    background: #05a179;
  }

  .phoneBadgeBad {
    background: #b91c3c;
  }
`;

function useIframeMessages(iframeRef, sourceKey) {
  const [data, setData] = useState(null);

  useEffect(() => {
    function onMessage(event) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!event.data || event.data.source !== sourceKey) return;
      setData(event.data);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sourceKey]);

  return [data, setData];
}

/* =========================================================================
   1. What is a scroll container? A minimal, live example.
   ========================================================================= */

export function ScrollContainerBasicExample() {
  const [overflow, setOverflow] = useState('visible');
  const [scrolled, setScrolled] = useState(false);
  const boxRef = useRef(null);
  const isContainer = overflow !== 'visible';

  function handleOverflowChange(event) {
    setOverflow(event.target.value);
    setScrolled(false);
    if (boxRef.current) boxRef.current.scrollTop = 0;
  }

  function handleBoxScroll() {
    setScrolled((boxRef.current?.scrollTop ?? 0) > 0);
  }

  return (
    <div className={styles.demo}>
      <div className={styles.topBar}>
        <div className={styles.inlineField}>
          <label htmlFor="scroll-container-overflow">overflow</label>
          <select id="scroll-container-overflow" value={overflow} onChange={handleOverflowChange}>
            <option value="visible">visible</option>
            <option value="auto">auto</option>
            <option value="scroll">scroll</option>
            <option value="hidden">hidden</option>
          </select>
          {overflow === 'hidden' && (
            <button
              type="button"
              className={styles.smallButton}
              onClick={() => boxRef.current?.scrollTo({ top: 90, behavior: 'smooth' })}
            >
              Scroll programmatically
            </button>
          )}
        </div>
        <div className={styles.badgeGroup}>
          <span className={`${styles.scrolledBadge} ${scrolled ? styles.scrolledBadgeVisible : ''}`}>Scrolled</span>
          <span
            className={`${styles.containerBadge} ${isContainer ? styles.containerBadgeYes : styles.containerBadgeNo}`}
          >
            {isContainer ? 'Scroll container' : 'Not a scroll container'}
          </span>
        </div>
      </div>
      <div ref={boxRef} className={styles.basicBox} style={{ overflow, height: '150px' }} onScroll={handleBoxScroll}>
        <p>This box has a fixed height of 150px, so this text overflows it.</p>
        <p>Change the overflow value above and see what happens to the overflowing content.</p>
        <p>
          With <code>hidden</code> you can&apos;t scroll it with your mouse or keyboard, but it&apos;s still a scroll
          container. Click the &quot;scroll programmatically&quot; button to see it scroll.
        </p>
        <p>This is the last line, overflowing the container.</p>
      </div>
    </div>
  );
}

/* =========================================================================
   1b. Background propagation from <body> to the viewport.
   ========================================================================= */

function buildBackgroundPropagationDoc(htmlBg, bodyBg) {
  const anyBg = htmlBg || bodyBg;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  html {
    ${htmlBg ? 'background: #05a179;' : ''}
  }
  body {
    margin: 0;
    ${bodyBg ? 'background: #0f63d8;' : ''}
    border: 3px dashed #16335c;
    font-family: system-ui, sans-serif;
    color: ${anyBg ? '#fff' : '#0f2a44'};
  }
  p { margin: 0; padding: 16px; font-size: 14px; line-height: 1.5; }
</style>
</head>
<body>
  <p>This is all the content in &lt;body&gt;. It's much shorter than the viewport.</p>
  <script>
    function report() {
      var htmlBgColor = getComputedStyle(document.documentElement).backgroundColor;
      var bodyBgColor = getComputedStyle(document.body).backgroundColor;
      var htmlHasBg = htmlBgColor !== 'rgba(0, 0, 0, 0)' && htmlBgColor !== 'transparent';
      var bodyHasBg = bodyBgColor !== 'rgba(0, 0, 0, 0)' && bodyBgColor !== 'transparent';
      var viewportSource = htmlHasBg ? 'html' : bodyHasBg ? 'body (pushed back)' : 'none';
      parent.postMessage({ source: 'root-scroller-background-demo', viewportSource: viewportSource }, '*');
    }
    window.addEventListener('load', report);
  </script>
</body>
</html>`;
}

export function BackgroundPropagationExample() {
  const [htmlBg, setHtmlBg] = useState(false);
  const [bodyBg, setBodyBg] = useState(false);
  const iframeRef = useRef(null);
  const [report] = useIframeMessages(iframeRef, 'root-scroller-background-demo');
  const srcDoc = useMemo(() => buildBackgroundPropagationDoc(htmlBg, bodyBg), [htmlBg, bodyBg]);

  return (
    <div className={styles.demo}>
      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Background propagation demo" />
        </div>
        <div className={styles.readoutStack}>
          <div className={styles.readout}>
            <div className={styles.readoutRow}>
              <label className={styles.readoutCheckboxLabel} htmlFor="body-bg">
                <input
                  id="body-bg"
                  className={styles.readoutCheckbox}
                  type="checkbox"
                  checked={bodyBg}
                  onChange={(event) => setBodyBg(event.target.checked)}
                />
                Add background to body
              </label>
            </div>
            <div className={styles.readoutRow}>
              <label className={styles.readoutCheckboxLabel} htmlFor="html-bg">
                <input
                  id="html-bg"
                  className={styles.readoutCheckbox}
                  type="checkbox"
                  checked={htmlBg}
                  onChange={(event) => setHtmlBg(event.target.checked)}
                />
                Add background to html
              </label>
            </div>
          </div>

          <div className={styles.readout}>
            <div className={styles.readoutRow}>
              <span className={styles.readoutLabel}>viewport background</span>
              <span className={styles.readoutValueNeutral}>{report ? report.viewportSource : '…'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   1c. The .page-wrapper opt-out pattern.
   ========================================================================= */

function buildPageWrapperDoc() {
  const paragraphs = Array.from({ length: 14 })
    .map((_, i) => `<p>Paragraph ${i + 1} of page content.</p>`)
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  html, body { height: 100%; overflow: hidden; margin: 0; }
  body { font-family: system-ui, sans-serif; color: #0f2a44; border:3px dashed dodgerblue;}
  .page-wrapper { height: 100vh; overflow: auto; padding: 14px 16px; }
  p { margin: 0 0 12px; line-height: 1.5; font-size: 14px; }
</style>
</head>
<body>
  <div class="page-wrapper">${paragraphs}</div>
  <script>
    document.addEventListener('scroll', function (e) {
      var isDocument = e.target === document;
      parent.postMessage({ source: 'root-scroller-pagewrapper-demo', isRoot: isDocument }, '*');
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function PageWrapperExample() {
  const iframeRef = useRef(null);
  const [report] = useIframeMessages(iframeRef, 'root-scroller-pagewrapper-demo');
  const srcDoc = useMemo(() => buildPageWrapperDoc(), []);

  return (
    <div className={styles.demo}>
      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Page wrapper scroll container demo" />
        </div>
        <div className={styles.readout}>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>scroll container</span>
            <span className={report?.isRoot === false ? styles.readoutValueBad : styles.readoutValueGood}>
              {report === null
                ? 'scroll to find out'
                : report.isRoot
                  ? '#document (root scroller)'
                  : '.page-wrapper (nested)'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   2. Overflow propagation from <body> to the viewport.
   ========================================================================= */

function buildPropagationDoc(htmlOverflow, bodyOverflow, fixedHeight) {
  const paragraphs = Array.from({ length: 16 })
    .map(
      (_, i) =>
        `<p>Paragraph ${i + 1}. Scroll this preview and watch the readout on the right to see which element actually receives the scroll.</p>`
    )
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  html {
    overflow-x: ${htmlOverflow};
    ${fixedHeight ? 'height: 100%;' : ''}
  }
  body {
    margin: 0;
    overflow-x: ${bodyOverflow};
    ${fixedHeight ? 'height: 100%;' : ''}
    border: 3px dashed #0f63d8;
    font-family: system-ui, sans-serif;
    color: #0f2a44;
  }
  .content { padding: 14px 16px; }
  p { margin: 0 0 12px; line-height: 1.5; font-size: 14px; }
</style>
</head>
<body>
  <div class="content">${paragraphs}</div>
  <script>
    function report(actualScroller) {
      var htmlStyle = getComputedStyle(document.documentElement);
      var bodyStyle = getComputedStyle(document.body);
      var htmlIsVisible = htmlStyle.overflowX === 'visible' && htmlStyle.overflowY === 'visible';
      var propagatedFrom = htmlIsVisible ? 'body' : 'html';
      var propagatedOverflowY = propagatedFrom === 'html' ? htmlStyle.overflowY : bodyStyle.overflowY;
      parent.postMessage({
        source: 'root-scroller-propagation-demo',
        htmlOverflowY: htmlStyle.overflowY,
        bodyOverflowY: bodyStyle.overflowY,
        propagatedFrom: propagatedFrom,
        propagatedOverflowY: propagatedOverflowY,
        actualScroller: actualScroller
      }, '*');
    }
    window.addEventListener('load', function () { report('(scroll to find out)'); });
    document.addEventListener('scroll', function (e) {
      var isDocument = e.target === document;
      report(isDocument ? '#document (root scroller)' : 'body (nested)');
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function OverflowPropagationExample() {
  const [htmlOverflow, setHtmlOverflow] = useState('visible');
  const [bodyOverflow, setBodyOverflow] = useState('visible');
  const [fixedHeight, setFixedHeight] = useState(false);
  const iframeRef = useRef(null);
  const [report] = useIframeMessages(iframeRef, 'root-scroller-propagation-demo');

  const srcDoc = useMemo(
    () => buildPropagationDoc(htmlOverflow, bodyOverflow, fixedHeight),
    [htmlOverflow, bodyOverflow, fixedHeight]
  );

  const codeExample = `html {
  overflow-x: ${htmlOverflow};${fixedHeight ? '\n  height: 100%;' : ''}
}

body {
  overflow-x: ${bodyOverflow};${fixedHeight ? '\n  height: 100%;' : ''}
}`;

  useEffect(() => {
    Prism.highlightAll();
  }, [codeExample]);

  const bodyIsScroller = report?.actualScroller === 'body (nested)';

  return (
    <div className={styles.demo}>
      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Overflow propagation demo" />
        </div>
        <div className={styles.readoutStack}>
          <div className={styles.readout}>
            <table className={styles.propagationTable}>
              <thead>
                <tr>
                  <th scope="col" aria-hidden="true" />
                  <th scope="col">overflow-x</th>
                  <th scope="col">overflow-y</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>html</td>
                  <td>
                    <select
                      className={styles.readoutSelect}
                      aria-label="html overflow-x"
                      value={htmlOverflow}
                      onChange={(event) => setHtmlOverflow(event.target.value)}
                    >
                      <option value="visible">visible</option>
                      <option value="hidden">hidden</option>
                    </select>
                  </td>
                  <td className={styles.readoutValueNeutral}>{report ? report.htmlOverflowY : '…'}</td>
                </tr>
                <tr>
                  <td>body</td>
                  <td>
                    <select
                      className={styles.readoutSelect}
                      aria-label="body overflow-x"
                      value={bodyOverflow}
                      onChange={(event) => setBodyOverflow(event.target.value)}
                    >
                      <option value="visible">visible</option>
                      <option value="hidden">hidden</option>
                    </select>
                  </td>
                  <td className={styles.readoutValueNeutral}>{report ? report.bodyOverflowY : '…'}</td>
                </tr>
                <tr>
                  <td>height</td>
                  <td colSpan={2} style={{ textAlign: 'left' }}>
                    <label className={styles.readoutCheckboxLabel} htmlFor="fixed-height">
                      <input
                        id="fixed-height"
                        className={styles.readoutCheckbox}
                        type="checkbox"
                        checked={fixedHeight}
                        onChange={(event) => setFixedHeight(event.target.checked)}
                      />
                      set to 100% on both
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.readout}>
            <div className={styles.readoutRow}>
              <span className={styles.readoutLabel}>scroll container</span>
              <span className={bodyIsScroller ? styles.readoutValueBad : styles.readoutValueGood}>
                {report ? report.actualScroller : '…'}
              </span>
            </div>
            <div className={styles.readoutRow}>
              <span className={styles.readoutLabel}>propagated to viewport</span>
              <span className={styles.readoutValueNeutral}>
                {report ? `${report.propagatedFrom} (${report.propagatedOverflowY})` : '…'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.codeBlockWrap}>
        <div className="gatsby-highlight">
          <pre className="language-css">
            <code>{codeExample}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   3. Mobile-only root scroller behavior: address bar collapse + scroll
      restoration, simulated in a phone frame.
   ========================================================================= */

export function MobileRootScrollerExample() {
  const [mode, setMode] = useState('root');
  const [scrollAmount, setScrollAmount] = useState(0);
  const [barProgress, setBarProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const dragRef = useRef({ active: false, lastY: 0 });
  const phoneRef = useRef(null);
  const pullDistanceRef = useRef(0);
  const wheelReleaseTimerRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  function setPull(value) {
    pullDistanceRef.current = value;
    setPullDistance(value);
  }

  function handleReload() {
    setReloadKey((current) => current + 1);
    if (mode !== 'root') {
      setScrollAmount(0);
      setBarProgress(0);
    }
  }

  function releasePull() {
    if (pullDistanceRef.current >= PULL_TRIGGER) {
      setRefreshing(true);
      handleReload();
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshing(false);
        setPull(0);
      }, 650);
    } else {
      setPull(0);
    }
  }

  function applyScroll(delta) {
    if (mode !== 'root') {
      setScrollAmount((prev) => Math.max(0, Math.min(CONTENT_HEIGHT, prev + delta)));
      return;
    }
    setScrollAmount((prev) => {
      const next = Math.max(0, Math.min(CONTENT_HEIGHT, prev + delta));
      if (next > 0) {
        if (pullDistanceRef.current !== 0) setPull(0);
      } else if (delta < 0) {
        setPull(Math.min(PULL_MAX, pullDistanceRef.current - delta));
      }
      return next;
    });
    setBarProgress((prev) => Math.max(0, Math.min(1, prev + delta / 90)));
  }

  function handlePointerDown(event) {
    if (event.target.closest('[data-no-drag]')) return;
    setIsDragging(true);
    dragRef.current = { active: true, lastY: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event) {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.lastY - event.clientY;
    dragRef.current.lastY = event.clientY;
    applyScroll(delta);
  }

  function handlePointerUp() {
    dragRef.current.active = false;
    setIsDragging(false);
    releasePull();
  }

  useEffect(() => {
    const el = phoneRef.current;
    if (!el) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      applyScroll(event.deltaY);
      if (wheelReleaseTimerRef.current) clearTimeout(wheelReleaseTimerRef.current);
      wheelReleaseTimerRef.current = setTimeout(releasePull, 220);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelReleaseTimerRef.current) clearTimeout(wheelReleaseTimerRef.current);
    };
  }, [mode]);

  useEffect(
    () => () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    },
    []
  );

  function handleModeChange(nextMode) {
    setMode(nextMode);
    setScrollAmount(0);
    setBarProgress(0);
    setPull(0);
  }

  function handleStatusTap() {
    if (mode === 'root') {
      setScrollAmount(0);
      setBarProgress(0);
    }
  }

  const cards = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`phone-card-${i}`} className={styles.phoneCard}>
          Card {i + 1}
        </div>
      )),
    []
  );

  const addressBarMarginTop = mode === 'root' ? -34 * barProgress : 0;

  return (
    <div className={styles.demo}>
      <div className={styles.topBar}>
        <div className={styles.segmentedToggle}>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${mode === 'root' ? styles.segmentedToggleBtnActiveGood : ''}`}
            onClick={() => handleModeChange('root')}
          >
            Root scroller
          </button>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${mode === 'nested' ? styles.segmentedToggleBtnActiveBad : ''}`}
            onClick={() => handleModeChange('nested')}
          >
            Nested scroll container
          </button>
        </div>
        <button type="button" className={styles.reloadButton} onClick={handleReload}>
          <svg
            key={reloadKey}
            className={styles.reloadIcon}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Reload page
        </button>
      </div>

      <div className={styles.phoneCol}>
        <div className={styles.phoneViewport}>
          <div
            ref={phoneRef}
            className={styles.phoneFrame}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className={styles.phoneScreen}>
              <div className={styles.phoneStatusBar} data-no-drag="true" onClick={handleStatusTap} role="presentation">
                <div className={styles.phoneNotch} />
              </div>
              <div className={styles.phoneAddressBar} style={{ marginTop: addressBarMarginTop }}>
                <div className={styles.phoneAddressBarBody} />
              </div>
              <div className={styles.phoneContentOuter}>
                {mode === 'nested' ? (
                  <div className={styles.phoneContentNestedBox}>
                    <div
                      className={styles.phoneContentInner}
                      style={{ transform: `translateY(${pullDistance - scrollAmount}px)` }}
                    >
                      {cards}
                    </div>
                  </div>
                ) : (
                  <div
                    className={styles.phoneContentInner}
                    style={{ transform: `translateY(${pullDistance - scrollAmount}px)` }}
                  >
                    <div
                      className={`${styles.phonePullIndicator} ${pullDistance > 0 ? styles.phonePullIndicatorVisible : ''} ${refreshing ? styles.phonePullIndicatorSpinning : ''}`}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        style={
                          refreshing ? undefined : { rotate: `${Math.round((pullDistance / PULL_TRIGGER) * 360)}deg` }
                        }
                      >
                        <path d="M23 4v6h-6" />
                        <path d="M1 20v-6h6" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                    </div>
                    {cards}
                  </div>
                )}
              </div>
              <div className={styles.phoneHomeIndicator} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   5. window.scrollY side by side: root scroller vs. a nested wrapper.
   ========================================================================= */

function buildScrollYDoc(variant) {
  const rows = Array.from({ length: 20 })
    .map((_, i) => `<div class="row">Item ${i + 1}</div>`)
    .join('');
  const isNested = variant === 'nested';
  const variantCss = isNested
    ? `html, body { height: 100%; margin: 0; overflow: hidden; } .wrapper { height: 100vh; overflow: auto; }`
    : `body { margin: 0; }`;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, sans-serif; }
  ${variantCss}
  .row { background: #fff; border: 1px solid #dce8ff; border-radius: 8px; padding: 8px 10px; margin: 6px 10px; font-size: 12px; color: #234; }
</style>
</head>
<body>
  ${isNested ? '<div class="wrapper">' : ''}
  <div style="padding: 6px 0;">${rows}</div>
  ${isNested ? '</div>' : ''}
  <script>
    var scrolledAtLeastOnce = false;
    function report() {
      parent.postMessage({
        source: 'root-scroller-scrolly-demo',
        scrollY: window.scrollY,
        hadScroll: scrolledAtLeastOnce
      }, '*');
    }
    window.addEventListener('load', report);
    document.addEventListener('scroll', function () {
      scrolledAtLeastOnce = true;
      report();
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function ScrollYSideBySideExample() {
  const rootIframeRef = useRef(null);
  const nestedIframeRef = useRef(null);
  const [rootReport] = useIframeMessages(rootIframeRef, 'root-scroller-scrolly-demo');
  const [nestedReport] = useIframeMessages(nestedIframeRef, 'root-scroller-scrolly-demo');

  const rootSrcDoc = useMemo(() => buildScrollYDoc('root'), []);
  const nestedSrcDoc = useMemo(() => buildScrollYDoc('nested'), []);

  const nestedStuck = nestedReport?.hadScroll && Math.round(nestedReport.scrollY) === 0;

  return (
    <div className={styles.demo}>
      <div className={styles.sideBySideGrid}>
        <div className={styles.sideBySidePane}>
          <span className={styles.sideBySideLabel}>Root scroller</span>
          <div className={styles.previewFrame}>
            <iframe ref={rootIframeRef} srcDoc={rootSrcDoc} title="Root scroller window.scrollY demo" />
          </div>
          <div className={styles.scrollYBox}>
            <div className={`${styles.scrollYValue} ${styles.scrollYValueGood}`}>
              {rootReport ? Math.round(rootReport.scrollY) : 0}px
            </div>
            <div className={styles.scrollYCaption}>window.scrollY</div>
          </div>
        </div>
        <div className={styles.sideBySidePane}>
          <span className={styles.sideBySideLabel}>Nested wrapper</span>
          <div className={styles.previewFrame}>
            <iframe ref={nestedIframeRef} srcDoc={nestedSrcDoc} title="Nested wrapper window.scrollY demo" />
          </div>
          <div className={styles.scrollYBox}>
            <div className={`${styles.scrollYValue} ${nestedStuck ? styles.scrollYValueBad : styles.scrollYValueGood}`}>
              {nestedReport ? Math.round(nestedReport.scrollY) : 0}px
            </div>
            <div className={styles.scrollYCaption}>
              window.scrollY{nestedStuck ? ' (remains at 0, even if you see the page scroll)' : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   6. Header + sidebar layout: fixed-height grid (broken) vs. sticky (fixed).
   ========================================================================= */

const SIDEBAR_SHARED_CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, sans-serif; color: #0f2a44; }
  :root { --header-height: 42px; }
  .app { display: grid; grid-template-columns: 100px 1fr; grid-template-rows: auto 1fr; }
  .header { grid-column: 1 / -1; background: #16335c; color: #fff; padding: 0 14px; display: flex; align-items: center; height: var(--header-height); font-weight: 700; font-size: 13px; }
  .sidebar { grid-row: 2 / -1; background: #eef5ff; padding: 10px; }
  .sidebar div { padding: 5px 0; font-size: 11px; color: #355; }
  .content { padding: 12px; }
  .card { background: #fff; border: 1px solid #dce8ff; border-radius: 10px; padding: 10px 12px; margin-bottom: 8px; font-size: 13px; box-shadow: 0 4px 14px rgba(18, 44, 89, 0.08); }
`;

const SIDEBAR_BROKEN_CSS = `
  .app { height: 100vh; }
  .content { overflow: auto; }
`;

const SIDEBAR_FIXED_CSS = `
  .header { position: sticky; top: 0; z-index: 5; align-self: start; }
  .sidebar { position: sticky; top: var(--header-height); align-self: start; height: calc(100vh - var(--header-height)); overflow: auto; }
`;

const SIDEBAR_NAV_SHORT = '<div>Dashboard</div><div>Projects</div><div>Settings</div><div>Help</div>';

function buildSidebarDoc(variant) {
  const variantCss = variant === 'broken' ? SIDEBAR_BROKEN_CSS : SIDEBAR_FIXED_CSS;
  const nav =
    variant === 'fixed-sidebar'
      ? Array.from({ length: 16 })
          .map((_, i) => `<div>Nav item ${i + 1}</div>`)
          .join('')
      : SIDEBAR_NAV_SHORT;
  const cards = Array.from({ length: 10 })
    .map((_, i) => `<div class="card">Card ${i + 1} — placeholder content.</div>`)
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>${SIDEBAR_SHARED_CSS}${variantCss}</style>
</head>
<body>
  <div class="app">
    <div class="header">My App</div>
    <div class="sidebar">${nav}</div>
    <div class="content">${cards}</div>
  </div>
  <script>
    document.addEventListener('scroll', function (e) {
      var kind = 'other';
      if (e.target === document) {
        kind = 'root';
      } else if (e.target.classList && e.target.classList.contains('content')) {
        kind = 'content';
      } else if (e.target.classList && e.target.classList.contains('sidebar')) {
        kind = 'sidebar';
      }
      parent.postMessage({ source: 'root-scroller-sidebar-demo', kind: kind }, '*');
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function HeaderSidebarStickyExample() {
  const [variant, setVariant] = useState('broken');
  const iframeRef = useRef(null);
  const [report, setReport] = useIframeMessages(iframeRef, 'root-scroller-sidebar-demo');
  const srcDoc = useMemo(() => buildSidebarDoc(variant), [variant]);

  useEffect(() => {
    setReport(null);
  }, [variant, setReport]);

  const codeExample =
    variant === 'broken'
      ? `.app {
  display: grid;
  grid-template-columns: 100px 1fr;
  grid-template-rows: auto 1fr;
  height:100vh;
}

.header {
  grid-column: 1 / -1;
}

.sidebar {
  grid-row: 2 / -1;
}

.content {
  overflow: auto;
}`
      : `.app {
  display: grid;
  grid-template-columns: 100px 1fr;
  grid-template-rows: auto 1fr;
}

.header {
  grid-column: 1 / -1;
  position: sticky;
  top: 0;
  z-index: 5;
  align-self: start;
}

.sidebar {
  grid-row: 2 / -1;
  position: sticky;
  top: var(--header-height);
  align-self: start;
  height: calc(100vh - var(--header-height));
  overflow: auto;
}`;

  useEffect(() => {
    Prism.highlightAll();
  }, [codeExample]);

  const kind = report?.kind;
  const readoutLabel =
    kind === 'root'
      ? '#document (root scroller) ✅'
      : kind === 'sidebar'
        ? '.sidebar (secondary, fine) ✅'
        : kind === 'content'
          ? '.content (nested) ⚠️'
          : 'scroll to find out';

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.segmentedToggle}>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${variant === 'broken' ? styles.segmentedToggleBtnActiveBad : ''}`}
            onClick={() => setVariant('broken')}
          >
            Broken: fixed-height grid
          </button>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${variant === 'fixed' ? styles.segmentedToggleBtnActiveGood : ''}`}
            onClick={() => setVariant('fixed')}
          >
            Fixed: sticky layout
          </button>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${variant === 'fixed-sidebar' ? styles.segmentedToggleBtnActiveGood : ''}`}
            onClick={() => setVariant('fixed-sidebar')}
          >
            Fixed, with scrollable sidebar
          </button>
        </div>
      </div>

      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Header and sidebar root scroller demo" />
        </div>
        <div className={styles.readout}>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>scroll event target</span>
            <span className={kind === 'content' ? styles.readoutValueBad : styles.readoutValueGood}>
              {readoutLabel}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.codeBlockWrap}>
        <div className="gatsby-highlight">
          <pre className="language-css">
            <code>{codeExample}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   7. Fill at least the full screen: height: 100vh (broken) vs. min-height:
      100svh (fixed) — identical with short content, different when it grows.
   ========================================================================= */

const FILL_SHARED_CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, sans-serif; color: #0f2a44; border: 3px dashed #0f63d8; }
  main { padding: 10px; }
  .card { background: #fff; border: 1px solid #dce8ff; border-radius: 10px; padding: 8px 10px; margin-bottom: 8px; font-size: 12px; box-shadow: 0 3px 10px rgba(18, 44, 89, 0.08); }
  .card.end { background: #16335c; color: #fff; text-align: center; font-weight: 700; }
`;

const FILL_BROKEN_CSS = `
  html, body { height: 100%; overflow: hidden; }
  .page-wrapper { display: flex; flex-direction: column; height: 100vh; }
  main { flex: 1; overflow: auto; }
`;

const FILL_FIXED_CSS = `
  .page-wrapper { display: flex; flex-direction: column; min-height: 100svh; }
  main { flex: 1; }
`;

function buildFillDoc(variant, length) {
  const variantCss = variant === 'broken' ? FILL_BROKEN_CSS : FILL_FIXED_CSS;
  const count = length === 'tall' ? 14 : 2;
  const cards = Array.from({ length: count })
    .map((_, i) => `<div class="card">Card ${i + 1}</div>`)
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>${FILL_SHARED_CSS}${variantCss}</style>
</head>
<body>
  <div class="page-wrapper">
    <main>${cards}<div class="card end">You reached the end 🎉</div></main>
  </div>
  <script>
    document.addEventListener('scroll', function (e) {
      var isDocument = e.target === document;
      parent.postMessage({ source: 'root-scroller-fill-demo', isRoot: isDocument }, '*');
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function FillScreenExample() {
  const [variant, setVariant] = useState('broken');
  const [length, setLength] = useState('short');
  const iframeRef = useRef(null);
  const [report, setReport] = useIframeMessages(iframeRef, 'root-scroller-fill-demo');
  const srcDoc = useMemo(() => buildFillDoc(variant, length), [variant, length]);

  useEffect(() => {
    setReport(null);
  }, [variant, length, setReport]);

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.segmentedToggle}>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${variant === 'broken' ? styles.segmentedToggleBtnActiveBad : ''}`}
            onClick={() => setVariant('broken')}
          >
            Broken: height: 100vh
          </button>
          <button
            type="button"
            className={`${styles.segmentedToggleBtn} ${variant === 'fixed' ? styles.segmentedToggleBtnActiveGood : ''}`}
            onClick={() => setVariant('fixed')}
          >
            Fixed: min-height: 100svh
          </button>
        </div>
        <div className={styles.field}>
          <label htmlFor="fill-length">content</label>
          <select id="fill-length" value={length} onChange={(event) => setLength(event.target.value)}>
            <option value="short">short (fits the screen)</option>
            <option value="tall">tall (taller than the screen)</option>
          </select>
        </div>
      </div>

      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Fill screen root scroller demo" />
        </div>
        <div className={styles.readout}>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>scroll event target</span>
            <span className={report?.isRoot === false ? styles.readoutValueBad : styles.readoutValueGood}>
              {report === null ? 'nothing scrolled yet' : report.isRoot ? '#document ✅' : 'main ⚠️'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   8. Getting rid of a horizontal scrollbar: overflow-x hidden vs. clip.
   ========================================================================= */

function buildHorizontalOverflowDoc(overflowXValue) {
  const paragraphs = Array.from({ length: 8 })
    .map((_, i) => `<p>Paragraph ${i + 1} of normal, vertically-scrolling content.</p>`)
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  html, body { height: 100%; overflow-x: ${overflowXValue}; }
  body { margin: 0; font-family: system-ui, sans-serif; color: #0f2a44; padding: 14px 16px; border: 3px dashed #0f63d8; }
  .wide { width: 130%; height: 40px; border-radius: 8px; margin-bottom: 14px; background: repeating-linear-gradient(45deg, #0f63d8, #0f63d8 10px, #05a179 10px, #05a179 20px); }
  p { margin: 0 0 12px; line-height: 1.5; font-size: 14px; }
</style>
</head>
<body>
  <div class="wide"></div>
  ${paragraphs}
  <script>
    function report(actualScroller) {
      parent.postMessage({
        source: 'root-scroller-horizontal-demo',
        bodyOverflowY: getComputedStyle(document.body).overflowY,
        actualScroller: actualScroller
      }, '*');
    }
    window.addEventListener('load', function () { report('(scroll to find out)'); });
    document.addEventListener('scroll', function (e) {
      var isDocument = e.target === document;
      report(isDocument ? '#document (root scroller)' : 'body (nested)');
    }, { capture: true });
  </script>
</body>
</html>`;
}

export function HorizontalOverflowExample() {
  const [overflowXValue, setOverflowXValue] = useState('hidden');
  const iframeRef = useRef(null);
  const [report] = useIframeMessages(iframeRef, 'root-scroller-horizontal-demo');
  const srcDoc = useMemo(() => buildHorizontalOverflowDoc(overflowXValue), [overflowXValue]);

  const codeExample = `html,
body {
  height: 100%;
  overflow-x: ${overflowXValue};
}`;

  useEffect(() => {
    Prism.highlightAll();
  }, [codeExample]);

  const bodyIsScroller = report?.actualScroller === 'body (nested)';

  return (
    <div className={styles.demo}>
      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Horizontal overflow demo" />
        </div>
        <div className={styles.readoutStack}>
          <div className={styles.readout}>
            <table className={styles.propagationTable}>
              <thead>
                <tr>
                  <th scope="col" aria-hidden="true" />
                  <th scope="col">overflow-x</th>
                  <th scope="col">overflow-y</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>body</td>
                  <td>
                    <select
                      className={styles.readoutSelect}
                      aria-label="body overflow-x"
                      value={overflowXValue}
                      onChange={(event) => setOverflowXValue(event.target.value)}
                    >
                      <option value="hidden">hidden</option>
                      <option value="clip">clip</option>
                    </select>
                  </td>
                  <td className={styles.readoutValueNeutral}>{report ? report.bodyOverflowY : '…'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.readout}>
            <div className={styles.readoutRow}>
              <span className={styles.readoutLabel}>scroll container</span>
              <span className={bodyIsScroller ? styles.readoutValueBad : styles.readoutValueGood}>
                {report ? report.actualScroller : '…'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.codeBlockWrap}>
        <div className="gatsby-highlight">
          <pre className="language-css">
            <code>{codeExample}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   9. Stopping the page from scrolling behind a modal, with :has().
   ========================================================================= */

function buildModalDoc(lockEnabled) {
  const cards = Array.from({ length: 10 })
    .map((_, i) => `<div class="card">Card ${i + 1} — background content.</div>`)
    .join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  html { scrollbar-gutter: stable; }
  ${lockEnabled ? 'html:has(dialog[open]) { overflow: hidden; }' : ''}
  body { margin: 0; font-family: system-ui, sans-serif; color: #0f2a44; }
  main { padding: 44px 12px 12px; }
  .card { background: #fff; border: 1px solid #dce8ff; border-radius: 10px; padding: 10px 12px; margin-bottom: 8px; font-size: 13px; box-shadow: 0 4px 14px rgba(18, 44, 89, 0.08); }
  .openBtn {
    position: fixed; top: 10px; left: 12px; z-index: 3;
    background: #0f63d8; color: #fff; border: 0; border-radius: 999px;
    padding: 8px 14px; font-weight: 700; font-size: 13px; cursor: pointer;
  }
  dialog { border: 0; border-radius: 14px; padding: 20px; max-width: 240px; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35); font-family: system-ui, sans-serif; }
  dialog p { margin: 0 0 12px; font-size: 13px; line-height: 1.5; color: #0f2a44; }
  dialog::backdrop { background: rgba(10, 20, 40, 0.5); }
  dialog button { background: #05a179; color: #fff; border: 0; border-radius: 8px; padding: 8px 14px; font-weight: 700; cursor: pointer; }
</style>
</head>
<body>
  <button type="button" class="openBtn" id="openBtn">Open dialog</button>
  <main>${cards}</main>
  <dialog id="modal">
    <p>Try scrolling (or dragging) the page behind this dialog.</p>
    <button type="button" id="closeBtn">Close</button>
  </dialog>
  <script>
    var modal = document.getElementById('modal');
    document.getElementById('openBtn').addEventListener('click', function () {
      modal.showModal();
      report();
    });
    document.getElementById('closeBtn').addEventListener('click', function () {
      modal.close();
    });
    modal.addEventListener('close', report);
    function report() {
      parent.postMessage({
        source: 'root-scroller-modal-demo',
        dialogOpen: modal.open,
        htmlOverflowY: getComputedStyle(document.documentElement).overflowY,
        scrollY: window.scrollY
      }, '*');
    }
    window.addEventListener('scroll', report, { capture: true });
    window.addEventListener('load', report);
    report();
  </script>
</body>
</html>`;
}

export function ModalLockExample() {
  const [lockEnabled, setLockEnabled] = useState(false);
  const iframeRef = useRef(null);
  const [report] = useIframeMessages(iframeRef, 'root-scroller-modal-demo');
  const srcDoc = useMemo(() => buildModalDoc(lockEnabled), [lockEnabled]);

  const codeExample = lockEnabled
    ? `html {
  scrollbar-gutter: stable;

  &:has(dialog[open]) {
    overflow: hidden;
  }
}`
    : `/* no lock — the page behind the dialog can still scroll */`;

  useEffect(() => {
    Prism.highlightAll();
  }, [codeExample]);

  const scrolledWhileOpen = report?.dialogOpen && report?.htmlOverflowY !== 'hidden' && report?.scrollY > 0;

  return (
    <div className={styles.demo}>
      <div className={styles.dialogControlsRow}>
        <label className={styles.checkboxField} htmlFor="modal-lock">
          <input
            id="modal-lock"
            type="checkbox"
            checked={lockEnabled}
            onChange={(event) => setLockEnabled(event.target.checked)}
          />
          Toggle scroll locking on the HTML
        </label>
      </div>

      <div className={styles.previewShell}>
        <div className={styles.previewFrame}>
          <iframe ref={iframeRef} srcDoc={srcDoc} title="Modal scroll lock demo" />
        </div>
        <div className={styles.readout}>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>dialog open</span>
            <span className={styles.readoutValueNeutral}>{report ? (report.dialogOpen ? 'yes' : 'no') : '…'}</span>
          </div>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>html overflow-y</span>
            <span className={styles.readoutValueNeutral}>{report ? report.htmlOverflowY : '…'}</span>
          </div>
          <div className={styles.readoutRow}>
            <span className={styles.readoutLabel}>scrollY</span>
            <span className={scrolledWhileOpen ? styles.readoutValueBad : styles.readoutValueGood}>
              {report ? Math.round(report.scrollY) : 0}px
            </span>
          </div>
        </div>
      </div>

      <div className={styles.codeBlockWrap}>
        <div className="gatsby-highlight">
          <pre className="language-css">
            <code>{codeExample}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}
